import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Pagination from '@mui/material/Pagination';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Alert from '@mui/material/Alert';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import SyncRounded from '@mui/icons-material/SyncRounded';
import InventoryRounded from '@mui/icons-material/InventoryRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import InputAdornment from '@mui/material/InputAdornment';
import PageHeader from '../components/common/PageHeader';
import storeLabel from '../utils/storeLabel';
import { useStores } from '../context/StoresContext';
import AddRounded from '@mui/icons-material/AddRounded';
import CheckBoxRounded from '@mui/icons-material/CheckBoxRounded';
import CheckBoxOutlineBlankRounded from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import FileDownloadRounded from '@mui/icons-material/FileDownloadRounded';
import exportRowsToXlsx from '../utils/exportXlsx';
import DeleteSweepRounded from '@mui/icons-material/DeleteSweepRounded';
import { getProducts, importProducts, importProductsCsv, getProductTypes, createProduct, bulkUpdateProductStoreStatus, deleteAllProducts, exportProductsJson } from '../services/productsApi';
import { getManufacturers } from '../services/manufacturersApi';
import { bulkSync, getSyncStatus } from '../services/syncApi';

const statusConfig = {
  synced: { color: '#00D9A6', label: 'Synced' },
  not_synced: { color: '#FF9800', label: 'Not Synced' },
  modified: { color: '#6C63FF', label: 'Modified' },
  error: { color: '#EF4444', label: 'Error' },
};

const shopifyStatusConfig = {
  active: { color: '#00D9A6', label: 'Active' },
  draft: { color: '#9CA3AF', label: 'Draft' },
  archived: { color: '#EF4444', label: 'Archived' },
  unknown: { color: '#D1D5DB', label: 'Unknown' },
};

const productStatusConfig = {
  ready: { color: '#00D9A6', label: 'Ready' },
  needs_attention: { color: '#FF9800', label: 'Needs Attention' },
};

const Products = () => {
  const navigate = useNavigate();
  const { activeStores, mainStore } = useStores();

  // Restore the last search/filters/page so the browser Back button returns you
  // to the same results instead of a fresh list. Persisted in sessionStorage.
  const persistedList = (() => {
    try { return JSON.parse(sessionStorage.getItem('productsListState') || '{}'); } catch { return {}; }
  })();

  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ page: persistedList.page || 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [search, setSearch] = useState(persistedList.search || '');
  const [filterType, setFilterType] = useState(persistedList.filterType || '');
  const [filterSyncStatus, setFilterSyncStatus] = useState(persistedList.filterSyncStatus || '');
  const [filterStoreId, setFilterStoreId] = useState(persistedList.filterStoreId || '');
  const [filterManufacturerId, setFilterManufacturerId] = useState(persistedList.filterManufacturerId || '');
  const [productTypes, setProductTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const searchTimeout = useRef(null);

  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importStore, setImportStore] = useState('');
  const [identifierType, setIdentifierType] = useState('sku');
  const [includeTypes, setIncludeTypes] = useState('');
  const [excludeTypes, setExcludeTypes] = useState('');
  const [includeTags, setIncludeTags] = useState('');
  const [excludeTags, setExcludeTags] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvImportProgress, setCsvImportProgress] = useState(null);
  const [csvOverwrite, setCsvOverwrite] = useState(true);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvSelectedFile, setCsvSelectedFile] = useState(null);
  const [filterProductStatus, setFilterProductStatus] = useState(persistedList.filterProductStatus || '');
  const [syncing, setSyncing] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncProgress, setSyncProgress] = useState(null);
  const pollRef = useRef(null);

  // Sync store selection dialog
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncDialogMode, setSyncDialogMode] = useState('page'); // 'page' or 'all'
  const [selectedSyncStores, setSelectedSyncStores] = useState([]);

  // Add product dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newProductTitle, setNewProductTitle] = useState('');
  const [newProductType, setNewProductType] = useState('');
  const [newProductDesc, setNewProductDesc] = useState('');
  const [addingProduct, setAddingProduct] = useState(false);
  const [addProductError, setAddProductError] = useState('');

  // Bulk selection
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [bulkStore, setBulkStore] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const toggleProductSelection = (id) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = products.map((p) => p.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedProducts.has(id));
    if (allSelected) {
      setSelectedProducts((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedProducts((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  // Publish = real Shopify push. Earlier this only flipped the local
  // product_stores.status to 'synced', which was misleading: it said
  // "published" while Shopify had no idea. Now it goes through bulkSync
  // with include_unlinked: true so it creates the Shopify product when
  // missing and updates when present. handleBulkPushSelected below does
  // the same; kept aliased so existing onClick handlers still resolve.
  const handleBulkPublish = () => runBulkSyncOrPush('push');

  const handleBulkUnpublish = async () => {
    if (!bulkStore || selectedProducts.size === 0) return;
    setBulkProcessing(true);
    try {
      await bulkUpdateProductStoreStatus(Array.from(selectedProducts), bulkStore.id, 'not_synced');
      setSelectedProducts(new Set());
      await fetchProducts(pagination.page, currentFilters());
    } catch (err) { console.error('Bulk unpublish failed:', err); }
    finally { setBulkProcessing(false); }
  };

  // Sync the selected products to the target store. Sync only updates
  // products already linked to a Shopify product on that store; unlinked
  // ones are silently skipped (that's the historical behavior). Reuses the
  // same bulkSync job tracker as "Sync All".
  const runBulkSyncOrPush = async (mode) => {
    if (!bulkStore || selectedProducts.size === 0) return;
    setBulkProcessing(true);
    setSyncingAll(true);
    setSyncResult(null);
    setSyncProgress(null);
    try {
      const productIds = Array.from(selectedProducts);
      const data = await bulkSync({
        productIds,
        storeIds: [bulkStore.id],
        includeUnlinked: mode === 'push',
      });
      setSyncResult({ severity: 'info', message: data.message });
      setSyncProgress({ total: data.total, completed: 0, synced: 0, errors: 0, status: 'running' });
      setSelectedProducts(new Set());
      startPolling();
    } catch (err) {
      const verb = mode === 'push' ? 'Push' : 'Sync';
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || `${verb} failed`;
      setSyncResult({ severity: 'error', message: msg });
      setSyncingAll(false);
    } finally {
      setBulkProcessing(false);
    }
  };
  const handleBulkSyncSelected = () => runBulkSyncOrPush('sync');
  const handleBulkPushSelected = () => runBulkSyncOrPush('push');

  const fetchProducts = useCallback(async (page = 1, filters = {}) => {
    try {
      const data = await getProducts(page, 25, filters);
      setProducts(data.products);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchProductTypes = useCallback(async () => {
    try {
      const types = await getProductTypes();
      setProductTypes(types);
    } catch (err) {
      console.error('Failed to fetch product types:', err);
    }
  }, []);

  const fetchManufacturers = useCallback(async () => {
    try {
      const data = await getManufacturers();
      setManufacturers(data.items || []);
    } catch (err) {
      console.error('Failed to fetch manufacturers:', err);
    }
  }, []);

  const currentFilters = useCallback(() => {
    const f = {};
    if (search.trim()) f.search = search.trim();
    if (filterType) f.product_type = filterType;
    if (filterSyncStatus && filterStoreId) {
      f.sync_status = filterSyncStatus;
      f.store_id = filterStoreId;
    }
    if (filterProductStatus) f.product_status = filterProductStatus;
    if (filterManufacturerId) f.manufacturer_id = filterManufacturerId;
    return f;
  }, [search, filterType, filterSyncStatus, filterStoreId, filterProductStatus, filterManufacturerId]);

  useEffect(() => {
    fetchProductTypes();
    fetchManufacturers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mount: fetch the restored page + filters once (so Back lands on the same
  // results). Subsequent filter changes reset to page 1.
  const didInitRef = useRef(false);
  useEffect(() => {
    fetchProducts(pagination.page, currentFilters());
    didInitRef.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!didInitRef.current) return;
    setRefreshing(true);
    fetchProducts(1, currentFilters());
  }, [filterType, filterSyncStatus, filterStoreId, filterProductStatus, filterManufacturerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist search/filters/page so the Back button restores the same view.
  useEffect(() => {
    try {
      sessionStorage.setItem('productsListState', JSON.stringify({
        search, filterType, filterSyncStatus, filterStoreId, filterManufacturerId, filterProductStatus,
        page: pagination.page,
      }));
    } catch { /* sessionStorage unavailable */ }
  }, [search, filterType, filterSyncStatus, filterStoreId, filterManufacturerId, filterProductStatus, pagination.page]);

  const handleSearchChange = (value) => {
    setSearch(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      const f = { search: value.trim(), product_type: filterType };
      if (filterSyncStatus && filterStoreId) {
        f.sync_status = filterSyncStatus;
        f.store_id = filterStoreId;
      }
      setRefreshing(true);
      fetchProducts(1, f);
    }, 400);
  };

  const handleImport = async () => {
    if (!importStore) return;

    setImporting(true);
    setImportResult(null);
    try {
      const params = {
        store_id: Number(importStore),
        identifier_type: identifierType,
      };

      if (includeTypes.trim()) {
        params.include_types = includeTypes.split(',').map((t) => t.trim()).filter(Boolean);
      }
      if (excludeTypes.trim()) {
        params.exclude_types = excludeTypes.split(',').map((t) => t.trim()).filter(Boolean);
      }
      if (includeTags.trim()) {
        params.include_tags = includeTags.split(',').map((t) => t.trim()).filter(Boolean);
      }
      if (excludeTags.trim()) {
        params.exclude_tags = excludeTags.split(',').map((t) => t.trim()).filter(Boolean);
      }

      const result = await importProducts(params, (progress) => {
        setImportResult({ message: progress.message });
      });

      if (result?.status === 'error') {
        setImportResult({ error: result.message });
      } else if (result?.status === 'done') {
        setImportResult(result);
      }
      await fetchProducts(pagination.page, currentFilters());
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Import failed';
      setImportResult({ error: msg });
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImport = () => {
    setImportOpen(false);
    setImportResult(null);
    setIncludeTypes('');
    setExcludeTypes('');
    setIncludeTags('');
    setExcludeTags('');
  };

  const openImportDialog = () => {
    if (mainStore) setImportStore(String(mainStore.id));
    setImportOpen(true);
  };

  const handleCsvImport = async (fileArg) => {
    const file = fileArg && fileArg.target ? fileArg.target.files?.[0] : fileArg;
    if (!file) return;
    setCsvImporting(true);
    setCsvImportProgress(null);
    try {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const probe = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: false });
      const headerRowIdx = probe.findIndex((r) => Array.isArray(r) && r.includes('_id'));
      const rows = XLSX.utils.sheet_to_json(ws, {
        defval: '',
        ...(headerRowIdx > 0 ? { range: headerRowIdx } : {}),
      });
      setCsvImportProgress({ status: 'uploading', message: `Parsed ${rows.length} rows, uploading...` });
      const result = await importProductsCsv(rows, (msg) => {
        setCsvImportProgress(msg);
      }, csvOverwrite);
      setCsvImportProgress(result);
      if (result?.status === 'done') {
        await fetchProducts(pagination.page);
      }
    } catch (err) {
      console.error('CSV import failed:', err);
      setCsvImportProgress({ status: 'error', message: err.message || 'CSV import failed' });
    } finally {
      setCsvImporting(false);
      const inputEl = document.getElementById('csv-import-file');
      if (inputEl) inputEl.value = '';
    }
  };

  // Open sync dialog for store selection
  const openSyncDialog = (mode) => {
    setSyncDialogMode(mode);
    setSelectedSyncStores(activeStores.map((s) => s.id)); // default: all stores selected
    setSyncDialogOpen(true);
  };

  // Sync only products on the current page
  const handleBulkSync = async () => {
    if (products.length === 0) return;
    setSyncDialogOpen(false);
    setSyncing(true);
    setSyncResult(null);
    try {
      const productIds = products.map((p) => p.id);
      await bulkSync({ productIds, storeIds: selectedSyncStores });
      setSyncResult({ severity: 'success', message: `Synced ${productIds.length} products (this page) to ${selectedSyncStores.length} store(s)` });
      await fetchProducts(pagination.page, currentFilters());
    } catch (err) {
      setSyncResult({ severity: 'error', message: 'Page sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  // Poll for bulk sync progress
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const status = await getSyncStatus();
        if (status.status === 'running') {
          setSyncProgress(status);
        } else if (status.status === 'done') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setSyncProgress(null);
          setSyncingAll(false);
          // When the backend reports errors, expose the *why*. errors_detail
          // is capped at 20 entries server-side; we render up to 5 inline and
          // hint at the rest so the alert stays readable.
          const details = Array.isArray(status.errors_detail) ? status.errors_detail : [];
          let message = `Sync complete: ${status.synced} synced, ${status.errors} errors (${status.total} products)`;
          if (details.length > 0) {
            const head = details.slice(0, 5).map((d) => {
              const where = d.store_id ? ` (store ${d.store_id})` : '';
              return `• Product ${d.product_id}${where}: ${d.message}`;
            }).join('\n');
            const more = details.length > 5 ? `\n…and ${details.length - 5} more (see Logs page for the full list).` : '';
            message += `\n\n${head}${more}`;
          }
          setSyncResult({
            severity: status.errors > 0 ? 'warning' : 'success',
            message,
          });
          fetchProducts(pagination.page, currentFilters());
        } else if (status.status === 'error') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setSyncProgress(null);
          setSyncingAll(false);
          setSyncResult({ severity: 'error', message: status.errorMessage || 'Sync failed' });
        } else {
          // idle — sync finished before polling started
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {
        // ignore poll errors
      }
    }, 2000);
  }, [fetchProducts, pagination.page, currentFilters]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Sync every product for the user (all pages) — non-blocking
  const handleSyncEverything = async () => {
    setSyncDialogOpen(false);
    setSyncingAll(true);
    setSyncResult(null);
    setSyncProgress(null);
    try {
      const data = await bulkSync({ all: true, storeIds: selectedSyncStores });
      setSyncResult({ severity: 'info', message: data.message });
      setSyncProgress({ total: data.total, completed: 0, synced: 0, errors: 0, status: 'running' });
      startPolling();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Full sync failed';
      setSyncResult({ severity: 'error', message: msg });
      setSyncingAll(false);
    }
  };

  const handleAddProduct = async () => {
    if (!newProductTitle.trim()) return;
    setAddingProduct(true);
    setAddProductError('');
    try {
      const product = await createProduct({
        title: newProductTitle.trim(),
        product_type: newProductType,
        description: newProductDesc,
      });
      setAddDialogOpen(false);
      setNewProductTitle('');
      setNewProductType('');
      setNewProductDesc('');
      navigate(`/products/${product.id}`);
    } catch (err) {
      console.error('Failed to create product:', err);
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to create product';
      setAddProductError(msg);
    } finally {
      setAddingProduct(false);
    }
  };

  const toggleSyncStore = (storeId) => {
    setSelectedSyncStores((prev) =>
      prev.includes(storeId) ? prev.filter((id) => id !== storeId) : [...prev, storeId]
    );
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Products" subtitle="Manage your product catalog across stores" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Thin top-of-page progress bar while re-fetching after a search/filter/page change.
          We deliberately do NOT unmount the search input — that's what caused the
          "search box freezes after each character" bug: the previous build returned a
          spinner-only page while loading, so every debounced fetch yanked focus off
          the input. */}
      {refreshing && <LinearProgress sx={{ position: 'sticky', top: 0, zIndex: 10 }} />}
      <PageHeader
        title="Products"
        subtitle="Manage your product catalog across stores"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {products.length > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={syncingAll ? <CircularProgress size={18} color="inherit" /> : <SyncRounded />}
                onClick={() => openSyncDialog('all')}
                disabled={syncingAll}
              >
                Sync All
              </Button>
            )}
            <Button
              variant="contained"
              color="success"
              startIcon={<AddRounded />}
              onClick={() => setAddDialogOpen(true)}
            >
              Add Product
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFileRounded />}
              onClick={() => { setCsvSelectedFile(null); setCsvDialogOpen(true); }}
              disabled={csvImporting}
            >
              {csvImporting ? 'Importing CSV...' : 'Import CSV'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadRounded />}
              onClick={async () => {
                try {
                  const { items: rows } = await exportProductsJson();
                  await exportRowsToXlsx({
                    rows,
                    columns: [
                      '_id', 'Name', 'SKU', 'Product Type', 'Description', 'Manufacturer',
                      'lead time', 'MOQ', 'Stackable', 'Family Member ID', 'Product Status',
                      'Additional Tags', 'Materials', 'Features', 'Certifications',
                      'Settings', 'Installations', 'Asana Inventory Link',
                    ],
                    filename: 'products',
                    sheetName: 'Products',
                  });
                } catch (err) {
                  console.error('Export failed:', err);
                  alert('Export failed: ' + (err.response?.data?.message || err.message));
                }
              }}
              disabled={products.length === 0}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepRounded />}
              onClick={async () => {
                if (!window.confirm('Delete ALL products? This cannot be undone.')) return;
                if (!window.confirm('Are you sure? This will delete all products permanently.')) return;
                try {
                  const result = await deleteAllProducts();
                  alert(result.message);
                  await fetchProducts(1, currentFilters());
                } catch (err) {
                  alert('Failed to delete: ' + (err.response?.data?.message || err.message));
                }
              }}
            >
              Delete All
            </Button>
            {false && (
              <Button
                variant="contained"
                startIcon={<DownloadRounded />}
                onClick={openImportDialog}
                disabled={activeStores.length === 0}
              >
                Get Products
              </Button>
            )}
          </Box>
        }
      />

      {csvImportProgress && (
        <Alert
          severity={csvImportProgress.status === 'done' ? 'success' : csvImportProgress.status === 'error' ? 'error' : 'info'}
          onClose={() => setCsvImportProgress(null)}
          sx={{ mb: 2 }}
        >
          {csvImportProgress.message || `Processing... ${csvImportProgress.processed || 0}/${csvImportProgress.total || '?'}`}
          {csvImportProgress.status === 'progress' && (
            <LinearProgress variant="determinate" value={(csvImportProgress.processed / csvImportProgress.total) * 100} sx={{ mt: 1 }} />
          )}
        </Alert>
      )}

      {syncResult && (
        <Alert
          severity={syncResult.severity}
          onClose={() => setSyncResult(null)}
          sx={{ mb: 2, whiteSpace: 'pre-wrap' }}
        >
          {syncResult.message}
        </Alert>
      )}

      {syncProgress && syncProgress.status === 'running' && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Syncing products...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {syncProgress.completed} / {syncProgress.total}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={syncProgress.total > 0 ? (syncProgress.completed / syncProgress.total) * 100 : 0}
          />
        </Box>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by title or ID..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Types</MenuItem>
          {productTypes.map((t) => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Sync Status"
          value={filterSyncStatus}
          onChange={(e) => setFilterSyncStatus(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="synced">Synced</MenuItem>
          <MenuItem value="not_synced">Not Synced</MenuItem>
          <MenuItem value="modified">Modified</MenuItem>
          <MenuItem value="error">Error</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={filterProductStatus}
          onChange={(e) => setFilterProductStatus(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="ready">Ready</MenuItem>
          <MenuItem value="needs_attention">Needs Attention</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Manufacturer"
          value={filterManufacturerId}
          onChange={(e) => setFilterManufacturerId(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Manufacturers</MenuItem>
          {manufacturers.map((m) => (
            <MenuItem key={m.id} value={String(m.id)}>{m.name}</MenuItem>
          ))}
        </TextField>
        {filterSyncStatus && (
          <TextField
            select
            size="small"
            label="Store"
            value={filterStoreId}
            onChange={(e) => setFilterStoreId(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Select Store</MenuItem>
            {activeStores.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {storeLabel(s)}
              </MenuItem>
            ))}
          </TextField>
        )}
        {(search || filterType || filterSyncStatus || filterProductStatus || filterManufacturerId) && (
          <Button
            size="small"
            onClick={() => {
              setSearch('');
              setFilterType('');
              setFilterSyncStatus('');
              setFilterStoreId('');
              setFilterProductStatus('');
              setFilterManufacturerId('');
              setRefreshing(true);
              fetchProducts(1);
            }}
          >
            Clear Filters
          </Button>
        )}
      </Box>

      {products.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <InventoryRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No products imported
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Get Products" to import products from your Shopify store
            </Typography>
          </Box>
        </Card>
      ) : (
        <>
          {/* Bulk Actions Bar */}
          {selectedProducts.size > 0 && (
            <Card sx={{ mb: 2, px: 2, py: 1.5, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedProducts.size} product{selectedProducts.size !== 1 ? 's' : ''} selected
                </Typography>
                <Autocomplete
                  size="small"
                  options={activeStores}
                  getOptionLabel={(o) => storeLabel(o)}
                  value={bulkStore}
                  onChange={(_, val) => setBulkStore(val)}
                  renderInput={(params) => <TextField {...params} label="Target Store" />}
                  sx={{ minWidth: 180 }}
                />
                <Button size="small" variant="contained" onClick={handleBulkPublish}
                  disabled={!bulkStore || bulkProcessing || syncingAll}
                  title="Push the selected products to the chosen store. Creates the Shopify product if it doesn't exist yet; otherwise updates it.">
                  Publish
                </Button>
                <Button size="small" variant="outlined" onClick={handleBulkUnpublish}
                  disabled={!bulkStore || bulkProcessing}>
                  Unpublish
                </Button>
                <Button size="small" variant="contained" color="secondary" onClick={handleBulkSyncSelected}
                  disabled={!bulkStore || bulkProcessing || syncingAll}
                  title="Update Shopify for products already linked to this store. Skips products that don't exist on Shopify yet.">
                  Sync
                </Button>
                <Button size="small" onClick={() => setSelectedProducts(new Set())}>
                  Clear
                </Button>
              </Box>
            </Card>
          )}

          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 42 }}>
                      <Checkbox
                        size="small"
                        checked={products.length > 0 && products.every((p) => selectedProducts.has(p.id))}
                        indeterminate={products.some((p) => selectedProducts.has(p.id)) && !products.every((p) => selectedProducts.has(p.id))}
                        onChange={toggleSelectAllOnPage}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: 60 }}>
                      Image
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                    {activeStores.map((store) => (
                      <TableCell
                        key={store.id}
                        sx={{ fontWeight: 600, color: 'text.secondary' }}
                      >
                        {storeLabel(store)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product) => {
                    // images can be an array of URL strings (older imports) or
                    // an array of {src} objects (Shopify shape). Handle both.
                    const firstImageRaw = product.images?.[0];
                    const firstImageSrc = typeof firstImageRaw === 'string' ? firstImageRaw : firstImageRaw?.src;
                    return (
                      <TableRow
                        key={product.id}
                        hover
                        sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                        // Cmd/Ctrl+click (or middle-click) opens the product in a
                        // new tab; a plain click navigates in place.
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey) {
                            window.open(`/products/${product.id}`, '_blank', 'noopener');
                          } else {
                            navigate(`/products/${product.id}`);
                          }
                        }}
                        onAuxClick={(e) => {
                          if (e.button === 1) {
                            e.preventDefault();
                            window.open(`/products/${product.id}`, '_blank', 'noopener');
                          }
                        }}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            size="small"
                            checked={selectedProducts.has(product.id)}
                            onChange={() => toggleProductSelection(product.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Avatar
                            variant="rounded"
                            src={firstImageSrc}
                            sx={{ width: 40, height: 40, bgcolor: 'grey.100' }}
                          >
                            <InventoryRounded sx={{ fontSize: 20, color: 'grey.400' }} />
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {product.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {product.product_type || '--'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {product.identifier_value || '--'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {product.sku || '--'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {product.product_status ? (
                            <Tooltip
                              title={
                                product.product_status === 'needs_attention' && product.internal_note
                                  ? product.internal_note
                                  : ''
                              }
                              placement="top"
                              componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-wrap', maxWidth: 320 } } }}
                            >
                              <Chip
                                label={(productStatusConfig[product.product_status] || {
                                  label: product.product_status.replace(/_/g, ' '),
                                  color: '#D1D5DB',
                                }).label}
                                size="small"
                                sx={{
                                  backgroundColor: alpha(
                                    (productStatusConfig[product.product_status] || { color: '#D1D5DB' }).color,
                                    0.14
                                  ),
                                  color: (productStatusConfig[product.product_status] || { color: '#6B7280' }).color,
                                  fontWeight: 600,
                                  borderRadius: 2,
                                  cursor: product.product_status === 'needs_attention' && product.internal_note ? 'help' : 'default',
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">--</Typography>
                          )}
                        </TableCell>
                        {activeStores.map((store) => {
                          const link = product.store_statuses?.find(
                            (s) => s.store_id === store.id
                          );
                          const cfg = statusConfig[link?.status] || statusConfig.not_synced;
                          const shopifyCfg = shopifyStatusConfig[link?.shopify_status] || shopifyStatusConfig.unknown;
                          return (
                            <TableCell key={store.id}>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                <Chip
                                  label={cfg.label}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(cfg.color, 0.12),
                                    color: cfg.color,
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                  }}
                                />
                                {link?.shopify_product_id && (
                                  <Chip
                                    label={shopifyCfg.label}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      color: shopifyCfg.color,
                                      borderColor: shopifyCfg.color,
                                      fontWeight: 500,
                                      fontSize: '0.65rem',
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={(_, page) => {
                  setRefreshing(true);
                  fetchProducts(page, currentFilters());
                }}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Import Products Dialog */}
      <Dialog open={importOpen} onClose={handleCloseImport} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Get Products from Shopify</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Store"
            value={importStore}
            onChange={(e) => setImportStore(e.target.value)}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
          >
            {activeStores.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {storeLabel(s)}
                {s.is_main ? ' (Main)' : ''}
              </MenuItem>
            ))}
          </TextField>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
              Product Identifier
            </FormLabel>
            <RadioGroup
              row
              value={identifierType}
              onChange={(e) => setIdentifierType(e.target.value)}
            >
              <FormControlLabel value="sku" control={<Radio size="small" />} label="SKU" />
              <FormControlLabel value="barcode" control={<Radio size="small" />} label="Barcode" />
            </RadioGroup>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Filters (optional, comma-separated)
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              size="small"
              label="Include Types"
              placeholder="e.g. Chair, Table"
              value={includeTypes}
              onChange={(e) => setIncludeTypes(e.target.value)}
            />
            <TextField
              size="small"
              label="Exclude Types"
              placeholder="e.g. Accessory"
              value={excludeTypes}
              onChange={(e) => setExcludeTypes(e.target.value)}
            />
            <TextField
              size="small"
              label="Include Tags"
              placeholder="e.g. featured, new"
              value={includeTags}
              onChange={(e) => setIncludeTags(e.target.value)}
            />
            <TextField
              size="small"
              label="Exclude Tags"
              placeholder="e.g. archive, draft"
              value={excludeTags}
              onChange={(e) => setExcludeTags(e.target.value)}
            />
          </Box>

          {importResult && !importResult.error && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {importResult.message}
            </Alert>
          )}
          {importResult?.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {importResult.error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseImport}>
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {!importResult && (
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importing || !importStore}
            >
              {importing ? <CircularProgress size={20} color="inherit" /> : 'Import'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvDialogOpen} onClose={() => !csvImporting && setCsvDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Import CSV / Excel</DialogTitle>
        <DialogContent>
          <input
            id="csv-import-file"
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => setCsvSelectedFile(e.target.files?.[0] || null)}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, mb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => document.getElementById('csv-import-file')?.click()}
            >
              Choose File
            </Button>
            <Typography variant="body2" color={csvSelectedFile ? 'text.primary' : 'text.secondary'}>
              {csvSelectedFile ? csvSelectedFile.name : 'No file selected'}
            </Typography>
          </Box>
          <FormControlLabel
            control={<Checkbox checked={csvOverwrite} onChange={(e) => setCsvOverwrite(e.target.checked)} />}
            label="Overwrite existing products"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            When enabled, rows whose handle matches an existing product will replace its fields. Disable to skip duplicates.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCsvDialogOpen(false)} disabled={csvImporting}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!csvSelectedFile || csvImporting}
            onClick={async () => {
              setCsvDialogOpen(false);
              await handleCsvImport(csvSelectedFile);
              setCsvSelectedFile(null);
            }}
          >
            {csvImporting ? <CircularProgress size={20} color="inherit" /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync Store Selection Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {syncDialogMode === 'page' ? 'Sync This Page' : 'Sync All Products'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which stores to sync to:
          </Typography>
          {activeStores.map((store) => (
            <Box
              key={store.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }}
              onClick={() => toggleSyncStore(store.id)}
            >
              {selectedSyncStores.includes(store.id)
                ? <CheckBoxRounded color="primary" />
                : <CheckBoxOutlineBlankRounded color="action" />
              }
              <Typography variant="body2">
                {storeLabel(store)}
                {store.is_main ? ' (Main)' : ''}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSyncDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={selectedSyncStores.length === 0}
            onClick={syncDialogMode === 'page' ? handleBulkSync : handleSyncEverything}
          >
            Sync to {selectedSyncStores.length} Store(s)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add Product</DialogTitle>
        <DialogContent>
          <TextField
            label="Product Title"
            value={newProductTitle}
            onChange={(e) => setNewProductTitle(e.target.value)}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            select
            label="Product Type"
            value={newProductType}
            onChange={(e) => setNewProductType(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="Chair">Chair</MenuItem>
            <MenuItem value="Table Base">Table Base</MenuItem>
            <MenuItem value="Table Top">Table Top</MenuItem>
            <MenuItem value="Barstool">Barstool</MenuItem>
            <MenuItem value="Booth">Booth</MenuItem>
            <MenuItem value="Accessory">Accessory</MenuItem>
          </TextField>
          <TextField
            label="Description"
            value={newProductDesc}
            onChange={(e) => setNewProductDesc(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
          {addProductError && (
            <Alert severity="error" sx={{ mt: 2 }}>{addProductError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddProduct}
            disabled={addingProduct || !newProductTitle.trim()}
          >
            {addingProduct ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;
